import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import './ResizableWorkspace.css';

// Requires react-resizable-panels v4 (Group / Panel / Separator API).
// Numeric sizes are pixels in v4 – use strings like "20%" for percentages.

const ORDER_KEY = 'layoutApp.panelOrder';
const SIZES_KEY = 'layoutApp.panelSizes';

function readStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    if (value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage blocked (private mode, iframe restrictions) – ignore
  }
}

// Keeps a saved order valid when panels are added/removed in code.
function sanitizeOrder(saved, ids) {
  if (!Array.isArray(saved)) return ids;
  const valid = saved.filter((id) => ids.includes(id));
  const missing = ids.filter((id) => !valid.includes(id));
  return [...valid, ...missing];
}

/**
 * panels: [{ id, title, defaultSize, minSize, render }]
 *   id          stable key, also used to persist sizes
 *   title       shown in the drag bar
 *   defaultSize e.g. "20%"
 *   minSize     e.g. "15%"
 *   render      () => ReactNode
 */
export default function ResizableWorkspace({ panels }) {
  const ids = useMemo(() => panels.map((p) => p.id), [panels]);
  const panelById = useMemo(
    () => Object.fromEntries(panels.map((p) => [p.id, p])),
    [panels]
  );

  const [order, setOrder] = useState(() => readStorage(ORDER_KEY, null));
  const [savedLayout, setSavedLayout] = useState(() => readStorage(SIZES_KEY, undefined));
  const [resetKey, setResetKey] = useState(0);
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { id, side: 'before' | 'after' }

  const effectiveOrder = sanitizeOrder(order, ids);

  useEffect(() => {
    if (order) writeStorage(ORDER_KEY, order);
  }, [order]);

  const handleLayoutChanged = useCallback((layout) => {
    writeStorage(SIZES_KEY, layout);
  }, []);

  const movePanel = useCallback(
    (sourceId, targetId, side) => {
      if (!sourceId || sourceId === targetId) return;
      setOrder((prev) => {
        const next = sanitizeOrder(prev, ids).filter((id) => id !== sourceId);
        const targetIndex = next.indexOf(targetId);
        next.splice(side === 'after' ? targetIndex + 1 : targetIndex, 0, sourceId);
        return next;
      });
    },
    [ids]
  );

  const resetLayout = () => {
    writeStorage(ORDER_KEY, undefined);
    writeStorage(SIZES_KEY, undefined);
    setOrder(null);
    setSavedLayout(undefined);
    setResetKey((k) => k + 1); // remount Group so default sizes apply again
  };

  // ---- drag & drop handlers ----
  const onDragStart = (id) => (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setDraggingId(id);
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  const onDragOver = (id) => (e) => {
    if (!draggingId) return; // ignore drags that did not start on a panel grip
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const side = e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
    if (dropTarget?.id !== id || dropTarget?.side !== side) {
      setDropTarget({ id, side });
    }
  };

  const onDrop = (id) => (e) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain') || draggingId;
    movePanel(sourceId, id, dropTarget?.side ?? 'before');
    onDragEnd();
  };

  return (
    <div className={`workspace${draggingId ? ' workspace--dragging' : ''}`}>
      {/* <div className="workspace__toolbar">
        <button type="button" className="workspace__reset" onClick={resetLayout}>
          Reset layout
        </button>
      </div> */}

      <Group
        key={resetKey}
        id="layout-app-workspace"
        orientation="horizontal"
        className="workspace__group"
        defaultLayout={savedLayout}
        onLayoutChanged={handleLayoutChanged}
      >
        {effectiveOrder.map((id, index) => {
          const panel = panelById[id];
          const isDragging = draggingId === id;
          const dropSide =
            dropTarget?.id === id && draggingId && draggingId !== id ? dropTarget.side : null;

          return (
            <Fragment key={id}>
              {index > 0 && <Separator className="workspace__separator" />}
              <Panel id={id} defaultSize={panel.defaultSize} minSize={panel.minSize}>
                <div
                  className={[
                    'workspace__panel',
                    isDragging && 'workspace__panel--dragging',
                    dropSide && `workspace__panel--drop-${dropSide}`,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onDragOver={onDragOver(id)}
                  onDragLeave={() => setDropTarget(null)}
                  onDrop={onDrop(id)}
                >
                  <div
                    className="workspace__panel-bar"
                    draggable
                    onDragStart={onDragStart(id)}
                    onDragEnd={onDragEnd}
                    title="Drag to rearrange"
                  >
                    <span className="workspace__grip" aria-hidden="true">⋮⋮</span>
                    <span className="workspace__panel-title">{panel.title}</span>
                  </div>
                  <div className="workspace__panel-body">{panel.render()}</div>
                </div>
              </Panel>
            </Fragment>
          );
        })}
      </Group>
    </div>
  );
}
